import { FiltersButton } from "@/components/Dataheader/FiltersButton";
import { FiltersDrawer } from "@/components/Dataheader/FiltersDrawer";
import { FC, ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import qs from "query-string";
import { DepositTypeEnum, TransactionTypeEnum, WithdrawTypeEnum } from "@payping/api";

import { Col, DatePicker, Form, Grid, Row, Select, Typography } from "@payping/web-components";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { submitQueryParams } from "@payping/utils/query-string";
import { dateFormats, toJalali, toUtc } from "@payping/utils/dateUtils";

import type { SelectProps } from "@payping/web-components/components/Select/types";
import type { RangePickerValue } from "@payping/web-components/components/DatePicker/generate/RangePicker";

type FiltersFormInputs = {
  transactionType: SelectProps["value"];
  depositType: SelectProps["value"];
  withdrawType: SelectProps["value"];
  createdOn: RangePickerValue;
};
type Props = {
  retry: () => void;
  loading: boolean;
  DownloadExcel: ReactNode;
};

const TransactionListFilters: FC<Props> = ({ loading, retry, DownloadExcel }) => {
  const { t } = useAppTranslation();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const location = useLocation();
  const parsedSearch = qs.parse(location.search, { parseNumbers: true });
  const [filterForm] = Form.useForm<FiltersFormInputs>();
  const navigate = useNavigate();
  const { xs } = Grid.useBreakpoint();

  const transactionTypeWatch = Form.useWatch("transactionType", filterForm);

  useEffect(() => {
    const newValue = {
      createdOn: [
        typeof parsedSearch.createdOnFrom === "string"
          ? toJalali({ date: parsedSearch.createdOnFrom, dateFormat: dateFormats.standard.utcDateTime }).object || null
          : null,
        typeof parsedSearch.createdOnTo === "string"
          ? toJalali({ date: parsedSearch.createdOnTo, dateFormat: dateFormats.standard.utcDateTime }).object || null
          : null,
      ],
      transactionType: TransactionTypeEnum[Number(parsedSearch?.transactionType)]
        ? Number(parsedSearch?.transactionType)
        : undefined,

      depositType: DepositTypeEnum[Number(parsedSearch?.depositType)] ? Number(parsedSearch?.depositType) : undefined,

      withdrawType: WithdrawTypeEnum[Number(parsedSearch?.withdrawType)]
        ? Number(parsedSearch?.withdrawType)
        : undefined,
    };

    filterForm.setFieldsValue(newValue);
  }, [
    filterForm,
    parsedSearch.createdOnFrom,
    parsedSearch.createdOnTo,
    parsedSearch?.depositType,
    parsedSearch?.transactionType,
    parsedSearch?.withdrawType,
  ]);

  const onReset = (): void => {
    submitQueryParams({
      navigate,
      newValues: {
        ...parsedSearch,
        transactionType: undefined,
        depositType: undefined,
        withdrawType: undefined,
        createdOnFrom: undefined,
        createdOnTo: undefined,
      },
      parsedSearch,
      pathname: location.pathname,
      retry,
    });

    filterForm.resetFields();
    setOpenDrawer(false);
  };

  return (
    <>
      <Form
        form={filterForm}
        onFinish={(values) => {
          const newValues = {
            transactionType: values.transactionType !== undefined ? String(values.transactionType) : undefined,
            createdOnFrom:
              values.createdOn && values.createdOn[0]
                ? toUtc({
                    date: values.createdOn[0],
                    toFormat: dateFormats.standard.utcDateTime,
                  }).string
                : undefined,
            createdOnTo:
              values.createdOn && values.createdOn[1]
                ? toUtc({
                    date: values.createdOn[1],
                    toFormat: dateFormats.standard.utcDateTime,
                  }).string
                : undefined,
            depositType: values.transactionType === TransactionTypeEnum.Deposit ? values.depositType : undefined,
            withdrawType: values.transactionType === TransactionTypeEnum.Withdraw ? values.withdrawType : undefined,
          };

          submitQueryParams({
            navigate,
            newValues: { ...parsedSearch, ...newValues },
            parsedSearch,
            pathname: location.pathname,
            retry,
          });

          setOpenDrawer(false);
        }}
      >
        <Row gutter={[8, 8]} style={{ marginTop: "32px", marginBottom: "32px" }} wrap>
          <Typography.Title className={"m-0"} level={4}>
            {t("settlement.transaction.title")}
          </Typography.Title>
        </Row>
        <Row gutter={[8, 8]} style={{ marginTop: "16px", marginBottom: "16px" }} wrap>
          <Col xs={xs ? 24 : undefined}>
            <Form.Item name="createdOn" label={t("date")} noStyle>
              <DatePicker.RangePicker name="createdOn" allowClear className="w-100" />
            </Form.Item>
          </Col>
          <Col xs={xs ? 12 : undefined}>
            <FiltersButton
              onClick={() => setOpenDrawer(true)}
              params={{
                transactionType: parsedSearch.transactionType,
                depositType: parsedSearch.depositType,
                withdrawType: parsedSearch.withdrawType,
                createdOnFrom: parsedSearch.createdOnFrom,
                createdOnTo: parsedSearch.createdOnTo,
              }}
            />
          </Col>

          <Col xs={xs ? 12 : undefined}>{DownloadExcel}</Col>
        </Row>

        {/* < FiltersDrawer > */}
        <FiltersDrawer
          title={t("common.filters")}
          onClose={() => setOpenDrawer(false)}
          open={openDrawer}
          onClickApplyFilters={() => filterForm.submit()}
          onClickResetFilters={() => onReset()}
          loading={loading}
        >
          <Form.Item name={"transactionType"} layout={"vertical"} label={t("settlement.transaction.transactionType")}>
            <Select
              value
              className={"tw-w-full"}
              allowClear
              options={[
                {
                  label: t("common.deposit"),
                  value: TransactionTypeEnum.Deposit,
                },
                {
                  label: t("common.withdraw"),
                  value: TransactionTypeEnum.Withdraw,
                },
              ]}
              popupMatchSelectWidth={false}
            />
          </Form.Item>

          {transactionTypeWatch === TransactionTypeEnum.Deposit ? (
            <Form.Item name={"depositType"} layout={"vertical"} label={t("settlement.transaction.depositType.title")}>
              <Select
                value
                className={"tw-w-full"}
                allowClear
                options={[
                  {
                    label: t("settlement.transaction.depositType.ShaparakSettlement"),
                    value: DepositTypeEnum.ShaparakSettlement,
                  },
                  {
                    label: t("settlement.transaction.depositType.IdentifiedDeposit"),
                    value: DepositTypeEnum.IdentifiedDeposit,
                  },
                  {
                    label: t("settlement.transaction.depositType.FailedSettlementAmountReturn"),
                    value: DepositTypeEnum.FailedSettlementAmountReturn,
                  },
                  {
                    label: t("settlement.transaction.depositType.FailedSettlementWageReturn"),
                    value: DepositTypeEnum.FailedSettlementWageReturn,
                  },
                  {
                    label: t("settlement.transaction.depositType.ManualDeposit"),
                    value: DepositTypeEnum.ManualDeposit,
                  },
                ]}
                popupMatchSelectWidth={false}
              />
            </Form.Item>
          ) : null}

          {transactionTypeWatch === TransactionTypeEnum.Withdraw ? (
            <Form.Item name={"withdrawType"} label={t("settlement.transaction.withdrawType.title")} layout={"vertical"}>
              <Select
                value
                className={"tw-w-full"}
                allowClear
                options={[
                  {
                    label: t("settlement.transaction.withdrawType.SettlementAmount"),
                    value: WithdrawTypeEnum.SettlementAmount,
                  },
                  {
                    label: t("settlement.transaction.withdrawType.SettlementWage"),
                    value: WithdrawTypeEnum.SettlementWage,
                  },
                  {
                    label: t("settlement.transaction.withdrawType.RequestWage"),
                    value: WithdrawTypeEnum.RequestWage,
                  },
                  {
                    label: t("settlement.transaction.withdrawType.IdentifiedDepositWage"),
                    value: WithdrawTypeEnum.IdentifiedDepositWage,
                  },
                  {
                    label: t("settlement.transaction.withdrawType.ManualWithdraw"),
                    value: WithdrawTypeEnum.ManualWithdraw,
                  },
                ]}
                popupMatchSelectWidth={false}
              />
            </Form.Item>
          ) : null}
        </FiltersDrawer>
        {/* </ FiltersDrawer > */}
      </Form>
    </>
  );
};

export default TransactionListFilters;
